import {Component, inject} from '@angular/core';
import {BaseModalComponent} from "../base-modal/base-modal.component";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {
  adminAndReviewerRoles,
  advertisementInitialStatisticsResourceType,
  defaultAdvertisementInitialStatisticsFilesStatus
} from "./configs/advertisement-initial-statistics-modal-config";
import {
  gender,
  getSelectDataByFieldName,
} from "./configs/advertisement-initial-statistics-select-data";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import {
  RecordData,
  RecordDataValue,
  RecordUpdateRequestParams,
} from "../../../types/records-table.types";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {CommonModule} from "@angular/common";
import {defaultRecordPropertyData} from "../../../models/default-record-property-data";
import {OpenFilesViewerModalParams} from "../../../types/file.types";
import {IconStatus} from "../../../models/icon-status-config";
import {IconStatusDirective} from "../../../../shared/directives/icon-status.directive";
import {FilesStatus} from "../../../types/files-status.types";
import {AdvertisementInitialStatisticsModalService} from "./services/advertisement-initial-statistics-modal.service";
import {
  AdvertisementFormField,
  AdvertisementInitialStatisticsDialogData
} from "./types/advertisement-initial-statistics-modal.types";
import {advertisementInitialStatisticsRowKeys} from "./configs/advertisement-initial-statistics-row-keys";
import {advertisementInitialStatisticsFormFields} from "./configs/advertisement-initial-statistics-form-fields";
import {FormRowKey} from "../../../types/form-field.types";
import { MatOption, MatSelect } from "@angular/material/select";
import { MatButton } from "@angular/material/button";

@Component({
  selector: 'app-advertisement-initial-statistics-modal',
  standalone: true,
  imports: [
    BaseModalComponent,
    MatFormField,
    MatInput,
    MatLabel,
    MatButtonToggleGroup,
    MatButtonToggle,
    CommonModule,
    IconStatusDirective,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatButton
  ],
  templateUrl: './advertisement-initial-statistics-modal.component.html',
  styleUrls: [
    './advertisement-initial-statistics-modal.component.scss',
    '../base-modal/base-modal.component.scss'
  ],
})
export class AdvertisementInitialStatisticsModalComponent extends BaseModalComponent {
  public dialogData = inject<AdvertisementInitialStatisticsDialogData>(MAT_DIALOG_DATA);
  private advertisementInitialStatisticsModalService: AdvertisementInitialStatisticsModalService = inject(AdvertisementInitialStatisticsModalService);

  public readonly advertisementInitialStatisticsRowKeys = advertisementInitialStatisticsRowKeys;
  public readonly getSelectDataByFieldName = getSelectDataByFieldName;
  public readonly advertisementInitialStatisticsResourceType = advertisementInitialStatisticsResourceType;
  public isUserAdminOrReviewer: boolean = false;
  public formGroup: FormGroup = new FormGroup({});
  public recordData: RecordData | null = null;
  public readonly gender = gender;
  public status: FilesStatus = defaultAdvertisementInitialStatisticsFilesStatus;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.checkUserAdminOrReviewer();
    this.modalStatesService.openModal(advertisementInitialStatisticsResourceType.path);
    this.formGroup = this.baseModalService.getFormGroup<AdvertisementFormField>(advertisementInitialStatisticsFormFields);
    await this.fetchAdvertisementInitialStatisticsData();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(advertisementInitialStatisticsResourceType.path);
  }

  public visibleRow(row: FormRowKey): boolean {
    return row.isHidden ? this.isUserAdminOrReviewer : true;
  }

  public getFieldsByRowKey(rowKey: string): Array<AdvertisementFormField> {
    return advertisementInitialStatisticsFormFields.filter(field => field.rowKey === rowKey);
  }

  public async handleRecordUpdate(key: string): Promise<void> {
    const control = this.formGroup.get(key);

    if (control?.errors) {
      return;
    }

    const newValue = this.formGroup.get(key)?.value;
    await this.updatedField(newValue, key);
  }

  public async handleChange(newValue: string, key: string): Promise<void> {
    await this.updatedField(newValue, key);
  }

  public preventDecimalInput(event: Event, key: string, useLimits: boolean = true ): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value === '') { return; }

    if (!useLimits) { return; }

    if (this.validationService.isInvalidNumberInput({value})) {
      this.handleInvalidInput(input, key);
    }
  }

  public formatBudgetValue(key: string, needMaximumFractionDigits: boolean = false): string | null {
    const value = this.formGroup.get(key)?.value ?? null;
    if (!value) { return null; }

    return this.utilityService.formatBudgetValue({
      value: value,
      showCurrencySymbol: false,
      ...(needMaximumFractionDigits &&  { maximumFractionDigits: 2 })
    });
  }

  public async handleFileAction(): Promise<void> {
    const openFilesViewerModalParams: OpenFilesViewerModalParams = {
      title: 'Первичная статистика',
      key: 'INITIAL_STATISTICS',
      recordId: this.baseModalService.getRecordCalendarId(this.dialogData.parentRecordData),
      parentPath: advertisementInitialStatisticsResourceType.path,
      recordCalendarId: this.baseModalService.getRecordCalendarId(this.dialogData.parentRecordData),
      isEditMode: this.isEditModeActive
    };

    if (!openFilesViewerModalParams.key) { return; }

    await this.modalsService.openSingleTypeFilesViewerModal(openFilesViewerModalParams);
    this.status = await this.advertisementInitialStatisticsModalService.getFilesStatus(Number(this.recordData?.id));
  }

  public fetchIconStatus(): IconStatus {
    return this.getIconStatus(this.status.initialStatisticsStatus);
  }

  private handleInvalidInput(input: HTMLInputElement, key: string): void {
    const lastValidValue = this.recordData?.[key]?.toString() ?? '';
    input.value = lastValidValue;
    this.formGroup.get(key)?.setValue(lastValidValue);
    this.formGroup.get(key)?.setErrors({ invalid: true })
    this.errorService.showErrorMessage('Ошибка! Укажите целое число от 0 до 100.');
  }

  private async fetchAdvertisementInitialStatisticsData(): Promise<void> {
    if (this.dialogData.initialAdvertisementStatisticsId) {
      this.status = await this.advertisementInitialStatisticsModalService.getFilesStatus(this.dialogData.initialAdvertisementStatisticsId);
    }

    this.recordData = await this.fetchRecordPropertyDataById<RecordData>({
      path: advertisementInitialStatisticsResourceType.path,
      resourceId: Number(this.dialogData.initialAdvertisementStatisticsId)
    }).catch(() => defaultRecordPropertyData);

    this.formGroup.patchValue(this.recordData);

    if (!this.formGroup.value['region']) {
      this.formGroup.get('region' as string)?.setValue('RUSSIA');
      await this.updatedField('RUSSIA', 'region');
    }
  }

  private async updatedField(newValue: RecordDataValue, key: string): Promise<void> {
    const recordUpdateRequestParams: RecordUpdateRequestParams = {
      path: advertisementInitialStatisticsResourceType.path,
      recordId: Number(this.dialogData.initialAdvertisementStatisticsId),
      fieldName: key,
      value: newValue
    }

    await this.updatedRecordField<RecordData>(recordUpdateRequestParams)
      .then(updatedRecord => {
        Object.assign(this.recordData!, updatedRecord);
        this.formGroup.patchValue(updatedRecord);
      })
      .catch(() => defaultRecordPropertyData)
  }

  private checkUserAdminOrReviewer(): void {
    const currentUserRoles = this.authService.getUserRoles();
    this.isUserAdminOrReviewer = adminAndReviewerRoles.some(i => currentUserRoles.includes(i));
  }
}
